import { parseISO } from "date-fns/index.js";

import * as yup from "yup";
import {
  isAtleast18,
  isFileWithinDimensionLimit,
  isValidUsername,
  isValidPhoneNumber,
} from "common/middleware/validation/validation.helpers";

yup.setLocale({
  mixed: {
    required: "required",
  },
  string: {
    email: "invalid_email",
    matches: "invalid_format",
  },
  number: {
    positive: "must_be_positive",
  },
});

export default yup;

const phoneNumberRegex = /^(\+\d{1}\s?)?1?\-?\.?\s?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;

// String
export const string = yup.string().nullable().trim();
export const email = string.trim().lowercase().email();
export const stringRequired = yup.string().nullable().trim().required();
export const stringNotRequired = yup.string().nullable().trim();
export const stringRequiredIfFieldHasXValue = (fieldName: string, values: any[]) => yup
  .string()
  .nullable()
  .when(fieldName, (ctx, schema) => (
    values.includes(ctx) ? schema.required() : schema
  ));
export const phoneNumberRequired = yup
  .string()
  .nullable()
  .trim()
  .required()
  .matches(phoneNumberRegex);
export const stringRequiredIfContextHasXValues = (ctx: string, values: any[]) => yup
  .string()
  .trim()
  .when(`$${ctx}`, (ctx, schema) => {
    return values.includes(ctx) ? schema.required() : schema;
  });
export const emailRequired = yup.string().trim().lowercase().email().required();
export const validUrl = yup
  .string()
  .nullable()
  .url("validUrl")
export const validUsername = yup
  .string()
  .nullable()
  .test("validUsername", isValidUsername);
export const stringRequiredIfFieldHasValue = (fieldName: string) => yup
  .string()
  .nullable()
  .trim()
  .when(fieldName, {
    is: val => Boolean(val),
    then: schema => schema.required(),
  });
export const stringMustBeAtleast18 = yup
  .string()
  .nullable()
  .required()
  .test(
    "minAge",
    "minAge",
    value => value ? isAtleast18(new Date(value.replace(/-/g, "/"))) : false,
  );
export const phoneNumber = yup
  .string()
  .nullable()
  .test("phoneNumberValidation", "invalidPhoneNumber", isValidPhoneNumber);
export const currency = string
  .transform(value => value ? value.replace(/,|\s/g, "") : value)
  .test("isValidCurrencyFormat", "invalidFormat", value => {
    if (!value) return true;

    const arr = value.split(".");

    return arr.length <= 2 && (arr[1] ? arr[1].length < 3 : true);
  });

// Number
export const number = yup
  .number()
  .transform((val) => isNaN(val) ? null : val)
  .nullable();
export const numberRequired = yup
  .number()
  .transform((val) => isNaN(val) ? null : val)
  .nullable()
  .required();
export const numberNotRequired = yup
  .number()
  .transform((val) => isNaN(val) ? null : val)
  .nullable();
export const numRequiredIfFieldHasXValue = (fieldName: string, values: any[]) => yup
  .number()
  .transform(val => isNaN(val) ? null : val)
  .nullable()
  .when(fieldName, (ctx, schema) => (
    values.includes(ctx) ? schema.required() : schema
  ));
export const minMaxNumRequiredIfFieldHasXValue = (
  fieldName: string,
  values: any[],
  min: number,
  max: number,
) => yup
  .number()
  .transform(val => isNaN(val) ? null : val)
  .nullable()
  .min(min, `minNum${min}`)
  .max(max, `maxNum${100}`)
  .when(fieldName, (ctx, schema) => (
    values.includes(ctx) ? schema.required() : schema
  ));
export const minNumRequiredIfFieldHasXValue = (
  fieldName: string,
  values: any[],
  min: number,
) => yup
  .number()
  .transform(val => isNaN(val) ? null : val)
  .nullable()
  .when(fieldName, (ctx, schema) => (
    values.includes(ctx)
      ? schema.required().min(min, `minNum${min}`)
      : schema
  ));
export const numRequiredIfContextHasXValues = (ctx: string, values: any[]) => yup
  .number()
  .transform((val) => isNaN(val) ? null : val)
  .nullable()
  .when(`$${ctx}`, (ctx, schema) => (
    values.includes(ctx) ? schema.required() : schema
  ));
export const minNumRequired = (min: number) => yup
  .number()
  .transform(val => isNaN(val) ? null : val)
  .nullable()
  .min(min, `minNum${min}`)
  .required();
export const minNumMaxNumRequired = (min: number, max: number) => yup
  .number()
  .transform(val => isNaN(val) ? null : val)
  .nullable()
  .min(min, `minNum${min}`)
  .max(max, `maxNum${max}`)
  .required();
export const minNumNotRequired = (min: number) => yup
  .number()
  .transform((val) => isNaN(val) ? null : val)
  .nullable()
  .min(min, `minNum${min}`);
export const minNumRequiredIfFieldHasValue = (fieldName: string, min: number) => yup
  .number()
  .transform(val => isNaN(val) ? null : val)
  .nullable()
  .when(fieldName, {
    is: val => Boolean(val),
    then: schema => schema
      .min(min, `minNum${min}`)
      .required(),
  });
export const minNumMaxNumRequiredIfFieldHasValue = (
  fieldName: string,
  min: number,
  max: number,
) => yup
  .number()
  .transform(val => isNaN(val) ? null : val)
  .nullable()
  .when(fieldName, {
    is: val => Boolean(val),
    then: schema => schema
      .min(min, `minNum${min}`)
      .max(max, `maxNum${max}`)
      .required(),
  });
export const isMinYear = (min: number) => number.min(min, `isMinYear${min}`);
export const isMaxCurrentYear = number.max(new Date().getFullYear(), "isMaxCurrentYear");

// Boolean
export const mustBeTrue = yup.boolean().oneOf([true]);

// Date
export const dateRequired = yup
  .date()
  .nullable()
  .transform((curr, orig) => orig === "" ? null : curr)
  .required();
export const minDateTodayRequired = yup
  .date()
  .nullable()
  .transform((curr, orig) => orig === "" ? null : curr)
  .min(new Date(new Date().setHours(0, 0, 0, 0)), "minDateToday")
  .required();
export const mustBeAtleast18 = yup
  .string()
  .nullable()
  .test(
    "minAge",
    "minAge",
    (value) => isAtleast18(parseISO(value))
  )
  .required();

//File
export const withinFileDimensionLimit = (maxHeight: number, maxWidth: number) => yup
  .mixed()
  .test(
    "maxFileDimensions",
    `maxFileDimensions${maxHeight}x${maxWidth}`,
    (file: FileList) => isFileWithinDimensionLimit(file, maxWidth, maxHeight)
  );
export const allowedFileTypes = yup
  .mixed()
  .test("fileType", "invalidFileType", (files: FileList, ctx) => {
    if (
      !files
      || files?.length === 0
      || !ctx.options.context?.allowedFileTypes
    ) {
      return true;
    }

    const { allowedFileTypes } = ctx.options.context;

    return Array.from(files).every(f => allowedFileTypes.includes(f.type));
  });

export const addressValidation = yup.object({
  unitNumber: yup.string().nullable(),
  street: stringRequired,
  city: stringRequired,
  province: stringRequired,
  postalCode: stringRequired,
  country: stringRequired,
});

export const depositValidation = yup.array().of(yup.object({
  amount: currency.required(),
  percentage: minNumMaxNumRequired(0, 100),
  dueDateDuration: number.min(1, "minNum1").when("$isAmendmentRequest", {
    is: (isAmendmentRequest: boolean) => !isAmendmentRequest,
    then: schema => schema.required(),
  }),
  dueDateDurationUnit: string.when("$isAmendmentRequest", {
    is: (isAmendmentRequest: boolean) => !isAmendmentRequest,
    then: schema => schema.required(),
  }),
  dueDate: string.when("$isAmendmentRequest", {
    is: true,
    then: schema => schema.required(),
  }),
  isRefundable: string.required(),
  paymentTypes: yup
    .array()
    .nullable()
    .min(1, "required")
    .required(),
}));
